import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  orderBy,
  limit,
} from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { Booking } from '../models/booking.model';
import { Review, REVIEW_MAX_REPLY_LENGTH, REVIEW_MAX_TEXT_LENGTH, REVIEW_MIN_TEXT_LENGTH } from '../models/review.model';
import { FirebaseAwareService } from '../shared/firestore/firebase-aware.service';

@Injectable({ providedIn: 'root' })
export class ReviewService extends FirebaseAwareService {
  private firestore = inject(Firestore);
  private auth = inject(Auth);

  async getReviewForBooking(bookingId: string): Promise<Review | null> {
    const snap = await this.inCtx(() => getDoc(doc(this.firestore, 'reviews', bookingId)));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() } as Review;
  }

  async getReviewsForSpace(spaceId: string, max = 20): Promise<Review[]> {
    const q = this.inCtx(() =>
      query(
        collection(this.firestore, 'reviews'),
        where('spaceId', '==', spaceId),
        orderBy('createdAt', 'desc'),
        limit(max),
      ),
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as Review));
  }

  async createReview(booking: Booking, rating: number, text: string): Promise<void> {
    const bookingId = booking.id?.trim();
    const uid = this.auth.currentUser?.uid;
    if (!bookingId || !uid) throw new Error('Sesión inválida.');
    if (booking.userId !== uid) throw new Error('No puedes valorar esta reserva.');
    if (booking.status !== 'COMPLETED') throw new Error('Solo puedes valorar reservas completadas.');

    const trimmed = text.trim();
    if (!Number.isInteger(rating) || rating < 1 || rating > 5) {
      throw new Error('La valoración debe ser entre 1 y 5 estrellas.');
    }
    if (trimmed.length < REVIEW_MIN_TEXT_LENGTH || trimmed.length > REVIEW_MAX_TEXT_LENGTH) {
      throw new Error(`El comentario debe tener entre ${REVIEW_MIN_TEXT_LENGTH} y ${REVIEW_MAX_TEXT_LENGTH} caracteres.`);
    }

    const existing = await this.getReviewForBooking(bookingId);
    if (existing) throw new Error('Ya has valorado esta reserva.');

    const ownerId = booking.ownerUid || booking.ownerId;
    if (!ownerId || !booking.spaceId) throw new Error('Reserva incompleta.');

    const guestDisplayName =
      booking.guestName?.trim() ||
      booking.userLegalName?.trim() ||
      this.auth.currentUser?.displayName?.trim() ||
      'Cliente';

    await this.inCtx(() =>
      setDoc(doc(this.firestore, 'reviews', bookingId), {
        bookingId,
        spaceId: booking.spaceId,
        userId: uid,
        ownerId,
        rating,
        text: trimmed,
        guestDisplayName,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }),
    );
  }

  async setOwnerReply(reviewId: string, reply: string, spaceOwnerId: string): Promise<void> {
    const uid = this.auth.currentUser?.uid;
    if (!uid || uid !== spaceOwnerId) throw new Error('No autorizado.');

    const trimmed = reply.trim();
    if (trimmed.length < 2 || trimmed.length > REVIEW_MAX_REPLY_LENGTH) {
      throw new Error(`La respuesta debe tener entre 2 y ${REVIEW_MAX_REPLY_LENGTH} caracteres.`);
    }

    const snap = await this.inCtx(() => getDoc(doc(this.firestore, 'reviews', reviewId)));
    if (!snap.exists()) throw new Error('Reseña no encontrada.');
    const data = snap.data() as Review;
    if (data.ownerId !== uid) throw new Error('No autorizado.');

    await this.inCtx(() =>
      updateDoc(doc(this.firestore, 'reviews', reviewId), {
        ownerReply: trimmed,
        ownerReplyAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }),
    );
  }
}
